// The chart's maths as d3 does it (logdash's chart is d3's): kept out of chart.tsx so a test can import it without JSX.

/** d3's tick step over [0, top]: about `count` of them, each 1, 2, 5 or 10 times a power of ten. */
export function tickStep(top: number, count: number): number {
  const step = top / count;
  const power = 10 ** Math.floor(Math.log10(step));
  const error = step / power;
  return power * (error >= Math.sqrt(50) ? 10 : error >= Math.sqrt(10) ? 5 : error >= Math.sqrt(2) ? 2 : 1);
}

/** The y axis' ticks as d3's `scaleLinear().domain([0, max(4, max)]).nice(4).ticks(4)`: the last is the axis' top. */
export function ticks(max: number): number[] {
  let top = Math.max(4, max);
  let step = 0;
  for (let i = 0; i < 10; i++) {
    const next = tickStep(top, 4);
    if (next === step) break;
    step = next;
    top = Math.ceil(top / step) * step;
  }
  return Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
}

/** d3's `curveMonotoneX` (Steffen) through the points: an SVG path, smooth, never overshooting a point. */
export function monotone(points: readonly (readonly [number, number])[]): string {
  const n = points.length;
  const p = (i: number) => points[i] as readonly [number, number];
  if (n < 3) return points.map(([x, y], i) => `${i ? 'L' : 'M'}${x},${y}`).join('');
  const slope = (i: number) => (p(i + 1)[1] - p(i)[1]) / (p(i + 1)[0] - p(i)[0]);
  const sign = (v: number) => (v < 0 ? -1 : 1);
  const t: number[] = [];
  for (let i = 1; i < n - 1; i++) {
    const h0 = p(i)[0] - p(i - 1)[0];
    const h1 = p(i + 1)[0] - p(i)[0];
    const [s0, s1] = [slope(i - 1), slope(i)];
    const mid = (s0 * h1 + s1 * h0) / (h0 + h1);
    t[i] = (sign(s0) + sign(s1)) * Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(mid)) || 0;
  }
  t[0] = (3 * slope(0) - (t[1] as number)) / 2;
  t[n - 1] = (3 * slope(n - 2) - (t[n - 2] as number)) / 2;
  let d = `M${p(0)[0]},${p(0)[1]}`;
  for (let i = 0; i < n - 1; i++) {
    const [[x0, y0], [x1, y1]] = [p(i), p(i + 1)];
    const dx = (x1 - x0) / 3;
    d += `C${x0 + dx},${y0 + dx * (t[i] as number)},${x1 - dx},${y1 - dx * (t[i + 1] as number)},${x1},${y1}`;
  }
  return d;
}
